import { useState, useEffect, useCallback } from "react";
import { RefreshCw, CheckCircle, AlertCircle, Copy, ExternalLink, Loader2, CalendarSync, Globe, Home } from "lucide-react";
import { getICalDashboard, triggerICalSync, triggerICalSyncAll, updateICalConfig, getICalConfig } from "@/lib/api";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function StatusBadge({ connected }) {
  return connected ? (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="status-connected">
      <CheckCircle className="w-3 h-3" /> Connecté
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-400 border border-gray-200" data-testid="status-disconnected">
      <AlertCircle className="w-3 h-3" /> Non configuré
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "Jamais";
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function timeSince(iso) {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `il y a ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days}j`;
}

function EditConfigModal({ item, onClose, onSaved }) {
  const [airbnb, setAirbnb] = useState("");
  const [booking, setBooking] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getICalConfig(item.logement_id).then(res => {
      setAirbnb(res.data.ical_airbnb_url || "");
      setBooking(res.data.ical_booking_url || "");
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [item.logement_id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateICalConfig(item.logement_id, {
        ical_airbnb_url: airbnb || null,
        ical_booking_url: booking || null,
      });
      toast.success("Configuration iCal mise à jour");
      onSaved();
      onClose();
    } catch {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="ical-config-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Configuration iCal</h3>
        <p className="text-sm text-gray-500 mb-5">{item.titre}</p>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">URL iCal Airbnb</label>
              <input
                type="url"
                value={airbnb}
                onChange={e => setAirbnb(e.target.value)}
                placeholder="https://www.airbnb.com/calendar/ical/..."
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                data-testid="ical-airbnb-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">URL iCal Booking.com</label>
              <input
                type="url"
                value={booking}
                onChange={e => setBooking(e.target.value)}
                placeholder="https://admin.booking.com/hotel/hoteladmin/ical.html?..."
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                data-testid="ical-booking-input"
              />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800">
                <strong>URL d'export</strong> (à coller dans Airbnb/Booking) :<br />
                <code className="text-xs break-all">{API_BASE}{item.export_url}</code>
              </p>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors">
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors"
            data-testid="ical-save-btn"
          >
            {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ICalSyncPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [syncingAll, setSyncingAll] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await getICalDashboard();
      setItems(res.data);
    } catch {
      toast.error("Erreur chargement du dashboard iCal");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSync = async (logementId) => {
    setSyncing(prev => ({ ...prev, [logementId]: true }));
    try {
      const res = await triggerICalSync(logementId);
      toast.success(`Synchro terminée : ${res.data.synced} événement(s) importé(s)`);
      fetchData();
    } catch {
      toast.error("Erreur de synchronisation");
    } finally {
      setSyncing(prev => ({ ...prev, [logementId]: false }));
    }
  };

  const handleSyncAll = async () => {
    setSyncingAll(true);
    try {
      await triggerICalSyncAll();
      toast.success("Synchronisation globale terminée");
      fetchData();
    } catch {
      toast.error("Erreur de synchronisation globale");
    } finally {
      setSyncingAll(false);
    }
  };

  const copyExportUrl = (url) => {
    navigator.clipboard.writeText(`${API_BASE}${url}`);
    toast.success("URL copiée dans le presse-papier");
  };

  const totalEvents = items.reduce((sum, i) => sum + i.synced_events, 0);
  const airbnbCount = items.filter(i => i.has_airbnb).length;
  const bookingCount = items.filter(i => i.has_booking).length;

  return (
    <div className="space-y-6" data-testid="ical-sync-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="ical-page-title">Synchronisation iCal</h1>
          <p className="text-sm text-gray-500 mt-1">Gérez les calendriers Airbnb et Booking.com de vos logements</p>
        </div>
        <button
          onClick={handleSyncAll}
          disabled={syncingAll || items.length === 0}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors shadow-sm"
          data-testid="sync-all-btn"
        >
          {syncingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Tout synchroniser
        </button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid="stat-logements">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
              <Home className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{items.length}</p>
              <p className="text-xs text-gray-500">Logements configurés</p>
            </div>
          </div>
          <div className="flex gap-3 mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
            <span>Airbnb : {airbnbCount}</span>
            <span>Booking : {bookingCount}</span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid="stat-events">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <CalendarSync className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{totalEvents}</p>
              <p className="text-xs text-gray-500">Événements importés</p>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-3 pt-3 border-t border-gray-100">Disponibilités synchronisées depuis les plateformes</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid="stat-frequency">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <Globe className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">1h</p>
              <p className="text-xs text-gray-500">Fréquence de synchro</p>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-3 pt-3 border-t border-gray-100">Synchronisation automatique toutes les heures</p>
        </div>
      </div>

      {/* Logements table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="ical-table">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Logements avec iCal</h2>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-gray-300" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 px-4">
            <CalendarSync className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Aucun logement configuré</p>
            <p className="text-gray-400 text-sm mt-1">Ajoutez des URLs iCal dans la fiche de chaque logement pour commencer la synchronisation.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Logement</th>
                  <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Airbnb</th>
                  <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Booking</th>
                  <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Événements</th>
                  <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Dernière synchro</th>
                  <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => (
                  <tr key={item.logement_id} className="hover:bg-gray-50/50 transition-colors" data-testid={`ical-row-${item.logement_id}`}>
                    <td className="px-5 py-4">
                      <p className="font-medium text-gray-900 text-sm">{item.titre}</p>
                      {item.ville && <p className="text-xs text-gray-400 mt-0.5">{item.ville}</p>}
                    </td>
                    <td className="px-5 py-4"><StatusBadge connected={item.has_airbnb} /></td>
                    <td className="px-5 py-4"><StatusBadge connected={item.has_booking} /></td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-md bg-gray-100 text-sm font-semibold text-gray-700">
                        {item.synced_events}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <p className="text-sm text-gray-700">{formatDate(item.ical_last_sync)}</p>
                      {item.ical_last_sync && (
                        <p className="text-xs text-gray-400">{timeSince(item.ical_last_sync)}</p>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => copyExportUrl(item.export_url)}
                          className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                          title="Copier l'URL d'export"
                          data-testid={`copy-url-${item.logement_id}`}
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditItem(item)}
                          className="p-2 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                          title="Configurer"
                          data-testid={`edit-config-${item.logement_id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleSync(item.logement_id)}
                          disabled={syncing[item.logement_id]}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 disabled:opacity-50 transition-colors"
                          data-testid={`sync-btn-${item.logement_id}`}
                        >
                          {syncing[item.logement_id] ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <RefreshCw className="w-3.5 h-3.5" />
                          )}
                          Sync
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editItem && (
        <EditConfigModal
          item={editItem}
          onClose={() => setEditItem(null)}
          onSaved={fetchData}
        />
      )}
    </div>
  );
}
